import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { useAuth } from 'contexts/AuthContext';
import { useTeam } from 'team/contexts/teamContext';
import { useTeamMembers } from 'team/hooks/useTeamMembers';
import {
    fetchTeamInsightsData,
    fetchTeamLeadsInsights,
    fetchTeamViewsInsights,
    fetchTeamLinkTapsInsights,
    fetchTeamRecentActivity
} from 'team/apis/teamInsightsApi';
import TeamInsightsHeader from 'team/components/insights/TeamInsightsHeader';
import TeamInsightsList from 'team/components/insights/TeamInsightsList';

const TeamInsightsContainer = () => {
    const { authState } = useAuth();
    const { currentTeamId, getCurrentTeam } = useTeam();
    const teamData = getCurrentTeam();
    const teamId = currentTeamId || teamData?._id;
    const { members } = useTeamMembers(teamData);
    const [currentView, setCurrentView] = useState('main');

    // Core data states (4 metrics)
    const [insightsData, setInsightsData] = useState(null);
    const [leadsData, setLeadsData] = useState(null);
    const [viewsData, setViewsData] = useState(null);
    const [linkTapsData, setLinkTapsData] = useState(null);
    const [recentActivity, setRecentActivity] = useState([]);

    // Date range state for calendar filter - default to Last 30 days
    const getLast30DaysRange = () => {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(endDate.getDate() - 29); // 30 days including today
        return [startDate, endDate];
    };

    const [dateRange, setDateRange] = useState(getLast30DaysRange());

    // State management
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Refs to keep track of component lifecycle and prevent race conditions
    const isMounted = useRef(true);

    // Process views data - similar to individual insights
    const processedViewsData = useCallback(() => {
        if (!viewsData) return { byType: {}, overTime: [] };

        const byType = {
            accessory: 0,
            qr_code: 0,
            share_link: 0
        };

        if (viewsData.byType) {
            byType.accessory = typeof viewsData.byType.accessory === 'number' ? viewsData.byType.accessory : 0;
            byType.qr_code = typeof viewsData.byType.qr_code === 'number' ? viewsData.byType.qr_code : 0;
            byType.share_link = typeof viewsData.byType.share_link === 'number' ? viewsData.byType.share_link : 0;
        }

        const overTime = Array.isArray(viewsData.overTime) ? viewsData.overTime : [];

        return {
            ...viewsData,
            byType,
            overTime
        };
    }, [viewsData]);

    // Handle date range changes from calendar filter
    const handleDateRangeChange = useCallback((startDate, endDate) => {
        console.log('Team insights date range changed:', { startDate, endDate });
        setDateRange([startDate, endDate]);
    }, []);

    // Fetch initial data
    const fetchInitialData = useCallback(async () => {
        // Prevent fetching if component is unmounted
        if (!isMounted.current) return;

        if (!teamId) {
            console.log('No teamId available, skipping data fetch');
            setLoading(false);
            return;
        }

        console.log('🔄 Starting data fetch for team insights page');
        setLoading(true);
        setError(null);

        // CRITICAL FIX: Store timeout ID for proper cleanup
        let timeoutId = null;
        const timeoutPromise = new Promise((_, reject) => {
            timeoutId = setTimeout(() => {
                console.warn('⚠️ Team insights data fetch timeout triggered after 30 seconds');
                reject(new Error('Data fetch timeout after 30 seconds'));
            }, 30000);
        });

        try {
            // Create date range object for API calls
            const dateRangeParam = {
                startDate: dateRange[0],
                endDate: dateRange[1]
            };

            // PERFORMANCE OPTIMIZATION: Load all data in parallel with timeout
            console.log('🚀 Loading all team insights data with date range:', dateRangeParam);
            const [
                insightsResponse,
                leadsResponse,
                viewsResponse,
                linkTapsResponse,
                activityResponse
            ] = await Promise.race([
                Promise.all([
                    fetchTeamInsightsData(teamId, dateRangeParam),
                    fetchTeamLeadsInsights(teamId, dateRangeParam),
                    fetchTeamViewsInsights(teamId, dateRangeParam),
                    fetchTeamLinkTapsInsights(teamId, dateRangeParam),
                    fetchTeamRecentActivity(teamId, dateRangeParam)
                ]),
                timeoutPromise
            ]);

            // Clear timeout on successful completion
            if (timeoutId) {
                clearTimeout(timeoutId);
                timeoutId = null;
            }

            // Check if component is still mounted before updating state
            if (!isMounted.current) {
                console.log('Component unmounted during data fetch, aborting state updates');
                return;
            }

            // Update all state at once to prevent multiple re-renders
            setInsightsData(insightsResponse.success && insightsResponse.data ? insightsResponse.data : { stats: {} });
            setLeadsData(leadsResponse.success && leadsResponse.data ? leadsResponse.data : { bySource: {}, overTime: [] });
            setViewsData(viewsResponse.success && viewsResponse.data ? viewsResponse.data : { byType: {}, overTime: [] });
            setLinkTapsData(linkTapsResponse.success && linkTapsResponse.data ? linkTapsResponse.data : { linkTaps: [], overTime: [] });
            setRecentActivity(activityResponse.success && activityResponse.data ? activityResponse.data : []);

            console.log('✅ All team insights data loaded successfully');

        } catch (fetchError) {
            // Clear timeout on error
            if (timeoutId) {
                clearTimeout(timeoutId);
                timeoutId = null;
            }

            // Prevent updating state if component is unmounted
            if (!isMounted.current) {
                console.log('Component unmounted during fetch error handling, aborting');
                return;
            }

            console.error('❌ Error fetching team insights data:', fetchError);

            // CRITICAL FIX: Provide fallback data on timeout/error to prevent infinite loading
            if (fetchError.message?.includes('timeout')) {
                console.warn('⚠️ Data fetch timed out, providing fallback data');
                setInsightsData({ stats: {} });
                setLeadsData({ bySource: {}, overTime: [] });
                setViewsData({ byType: {}, overTime: [] });
                setLinkTapsData({ linkTaps: [], overTime: [] });
                setRecentActivity([]);
            }

            setError(fetchError);
        } finally {
            // Prevent updating state if component is unmounted
            if (!isMounted.current) {
                console.log('Component unmounted during fetch completion, aborting state updates');
                return;
            }

            // CRITICAL FIX: Always stop loading, even on timeout/error
            setLoading(false);
        }
    }, [teamId, dateRange]);

    // Calculate active team members count - MUST be before any early returns
    const activeTeamMembersCount = React.useMemo(() => {
        if (!members || !Array.isArray(members)) return 0;
        return members.filter(member => member.status === 'active').length;
    }, [members]);

    // Initialize component and fetch data
    useEffect(() => {
        // Reset mounted state
        isMounted.current = true;

        if (!teamId) {
            console.log('No teamId available, skipping data fetch');
            setLoading(false);
            return;
        }

        console.log('🔄 TeamInsightsContainer mounted, initializing...');

        // Initialize state
        setError(null);
        setLoading(true);

        // Fetch initial data
        fetchInitialData()
            .catch(error => {
                if (!isMounted.current) return;
                console.error("❌ Error in initial data fetch:", error);
                if (error.message !== 'Component unmounted') {
                    setError(error);
                    setLoading(false);
                }
            });

        // Cleanup function
        return () => {
            console.log('🧹 Cleaning up TeamInsightsContainer...');
            isMounted.current = false;
        };
    }, [teamId, fetchInitialData]);

    // Refetch data when date range changes
    useEffect(() => {
        // Only refetch if we already have data loaded (not initial load)
        if (teamId && !loading && insightsData !== null) {
            console.log('📅 Date range changed, refetching team insights data...');
            fetchInitialData();
        }
    }, [dateRange, teamId, fetchInitialData]);

    // Render loading state
    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh'
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    // Render error state
    if (error) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                    textAlign: 'center'
                }}
            >
                <Typography variant="h6" color="error">
                    Error Loading Team Insights
                </Typography>
                <Typography variant="body2">
                    {error.message || 'An unexpected error occurred'}
                </Typography>
                <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ mt: 2, cursor: 'pointer' }}
                    onClick={fetchInitialData}
                >
                    Retry Loading
                </Typography>
            </Box>
        );
    }

    // Get properly formatted views data
    const processedViews = processedViewsData();

    // Create safe stats object to prevent null access
    const safeStats = {
        leadsGenerated: insightsData?.stats?.leadsGenerated || { value: 0, percentage: 0, trend: 'neutral' },
        linkTaps: insightsData?.stats?.linkTaps || { value: 0, percentage: 0, trend: 'neutral' },
        cardViews: insightsData?.stats?.cardViews || { value: 0, percentage: 0, trend: 'neutral' },
        contactsDownloaded: insightsData?.stats?.contactsDownloaded || { value: 0, percentage: 0, trend: 'neutral' }
    };

    // Main insights view - Components will be built separately
    return (
        <Box sx={{ padding: '20px' }}>
            <TeamInsightsHeader
                onDateRangeChange={handleDateRangeChange}
                dateRange={dateRange}
                teamName={teamData?.name || 'Team'}
            />

            {/* Placeholder for components - will be built separately */}
            <TeamInsightsList
                stats={safeStats}
                leadsData={leadsData}
                viewsData={processedViews}
                linkTapsData={linkTapsData}
                recentActivity={recentActivity}
                activeTeamMembersCount={activeTeamMembersCount}
                members={members}
            />
        </Box>
    );
};

export default TeamInsightsContainer;
